import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES, MARKS} from '@contentful/rich-text-types'
import type {HeadingSizes} from '@primer/react-brand'
import {SectionIntro, Image} from '@primer/react-brand'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import type {PrimerComponentSectionIntro} from '../../../schemas/contentful/contentTypes/primerComponentSectionIntro'
import {documentToPlainTextString, getAnalyticsEvent} from '../../../lib/utils/analytics'
import {getPrimerIcon} from '../../../lib/utils/icons'
import {getImageSources} from '../../../lib/utils/images'
import {ContentfulInlineFootnote} from '../ContentfulFootnotes/ContentfulInlineFootnote'

// Generic link texts that need descriptive aria-labels for accessibility (WCAG 2.4.4)
const GENERIC_LINK_TEXTS = ['view all', 'learn more', 'read more', 'see more', 'more']

function isGenericLinkText(text: string): boolean {
  return GENERIC_LINK_TEXTS.includes(text.toLowerCase().trim())
}

export type ContentfulSectionIntroProps = {
  component: PrimerComponentSectionIntro

  fullWidth?: boolean
  className?: string
  headingSize?: (typeof HeadingSizes)[number]
}

export function ContentfulSectionIntro({component, fullWidth, headingSize, className}: ContentfulSectionIntroProps) {
  const {label, leadingComponent, leadingComponentSize} = component.fields
  const footnotesEnabled = isFeatureEnabled('contentful_lp_footnotes')
  const Octicon = getPrimerIcon(label?.fields.icon)

  const analyticsLocation =
    typeof component.fields.heading === 'string'
      ? component.fields.heading
      : documentToPlainTextString(component.fields.heading)

  return (
    <SectionIntro
      className={className}
      align={component.fields.align}
      fullWidth={fullWidth ?? component.fields.fullWidth}
      id={component.fields.htmlId}
      leadingComponent={
        leadingComponent
          ? () => (
              <Image
                as="picture"
                src={`${leadingComponent.fields.file.url}?fm=webp`}
                sources={getImageSources(leadingComponent.fields.file.url, {maxWidth: 64})}
                alt={leadingComponent.fields.description || ''}
                loading="lazy"
                width={leadingComponentSize && leadingComponentSize === 'large' ? 64 : 40}
                height={leadingComponentSize && leadingComponentSize === 'large' ? 64 : 40}
                className="d-block"
                style={{display: 'block'}}
              />
            )
          : undefined
      }
    >
      {label && (
        <SectionIntro.Label
          size={label.fields.size}
          color={label.fields.color}
          // eslint-disable-next-line react-hooks/static-components
          {...(Octicon ? {leadingVisual: <Octicon />} : {})}
        >
          {label.fields.text}
        </SectionIntro.Label>
      )}
      <SectionIntro.Heading size={headingSize}>
        {typeof component.fields.heading === 'string'
          ? component.fields.heading
          : documentToReactComponents(component.fields.heading, {
              renderMark: {
                /**
                 * We use <em> to benefit from Primer Brand's special styling for <em> tags:
                 * https://primer.style/brand/components/SectionIntro#emphasized-text
                 */
                [MARKS.BOLD]: text => <em>{text}</em>,
              },
              renderNode: {
                [BLOCKS.PARAGRAPH]: (_, children) => [children, <br key="line-break" />],
                [INLINES.EMBEDDED_ENTRY]: node => {
                  if (footnotesEnabled && node.data.target.sys.contentType.sys.id === 'inlineFootnote') {
                    return (
                      <ContentfulInlineFootnote
                        component={node.data.target}
                        analyticsLocation={analyticsLocation}
                        providedInstanceId={analyticsLocation}
                        size="large"
                      />
                    )
                  }

                  return null
                },
              },
            })}
      </SectionIntro.Heading>
      {component.fields.description !== undefined ? (
        <SectionIntro.Description>
          {documentToReactComponents(component.fields.description, {
            renderNode: {
              [BLOCKS.PARAGRAPH]: (_, children) => [children, <br key="line-break" />],
              [INLINES.EMBEDDED_ENTRY]: node => {
                if (footnotesEnabled && node.data.target.sys.contentType.sys.id === 'inlineFootnote') {
                  return <ContentfulInlineFootnote component={node.data.target} analyticsLocation={analyticsLocation} />
                }

                return null
              },
            },
          })}
        </SectionIntro.Description>
      ) : null}

      {component.fields.link !== undefined && (
        <SectionIntro.Link
          variant={component.fields.linkVariant ?? 'accent'}
          href={component.fields.link.fields.href}
          data-ref={`section-intro-link-${component.fields.link.sys.id}`}
          {...(isGenericLinkText(component.fields.link.fields.text)
            ? {'aria-label': `${component.fields.link.fields.text} ${analyticsLocation}`}
            : {})}
          {...getAnalyticsEvent({
            action: component.fields.link.fields.text,
            tag: 'link',
            context: 'section_intro',
            location: analyticsLocation,
          })}
        >
          {component.fields.link.fields.text}
        </SectionIntro.Link>
      )}
    </SectionIntro>
  )
}

try{ ContentfulSectionIntro.displayName ||= 'ContentfulSectionIntro' } catch {}
try{ Octicon.displayName ||= 'Octicon' } catch {}